import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiClock, FiExternalLink, FiMessageCircle } from 'react-icons/fi';
import { useSector } from '../../contexts/SectorContext';
import { getColumns, type Column } from '../../services/ColumnService';
import { getCards, getCardColumnStats, getRevenueRadar, type Card, type RevenueRadar as RevenueRadarData } from '../../services/CardService';
import {
  listColumnConditions,
  listLatestMoves,
  type CrmColumnCondition,
  type CrmMoveLog,
} from '../../services/CrmAutomationService';
import CardMoveBadge from '../../components/crm/CardMoveBadge';
import RevenueRadar, { type RadarList } from '../components/revenue/RevenueRadar';
import TemperatureChip from '../components/revenue/TemperatureChip';
import { daysLabel, formatBrl, formatMoney } from '../../utils/revenueFormat';
import { columnKindLabel, effectiveColumnKind, isToday, truncateText } from '../../utils/crmMoveLabels';
import '../styles/mendsAi.css';

/** Cards por coluna na leitura (o CRM completo fica em /crm). */
const CARDS_PER_COLUMN = 30;

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function avatarTone(name: string) {
  const tones = ['is-a', 'is-b', 'is-c', 'is-d', 'is-e'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i) * (i + 1)) % tones.length;
  return tones[h];
}

interface FunnelBoard {
  columns: Column[];
  cards: Card[];
  counts: Record<string, number>;
  /** columnId → soma em R$ dos cards da coluna (todos, não só os carregados). */
  valueBrl: Record<string, number>;
  conditions: Map<string, CrmColumnCondition>;
  moves: Map<number, CrmMoveLog>;
}

const EMPTY_BOARD: FunnelBoard = {
  columns: [],
  cards: [],
  counts: {},
  valueBrl: {},
  conditions: new Map(),
  moves: new Map(),
};

/**
 * Leitura do funil = o próprio Kanban do CRM (mesmas colunas e cards), com a evidência
 * de cada movimentação feita pela IA. Edição (arrastar, condições) fica no CRM.
 */
const OpportunitiesPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { selectedSectorId } = useSector();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const radarParam = searchParams.get('radar');
  const initialList: RadarList | null =
    radarParam === 'paradas' || radarParam === 'sem-valor' || radarParam === 'fechados' ? radarParam : null;
  const [board, setBoard] = useState<FunnelBoard>(EMPTY_BOARD);
  const [radar, setRadar] = useState<RevenueRadarData | null>(null);
  const [radarError, setRadarError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Descarta respostas de cargas antigas (troca rápida de setor). */
  const loadSeqRef = useRef(0);
  const loadedSectorRef = useRef<number | null>(null);

  useEffect(() => {
    const seq = ++loadSeqRef.current;
    const isCurrent = () => seq === loadSeqRef.current;
    if (!selectedSectorId) {
      setBoard(EMPTY_BOARD);
      setLoading(false);
      loadedSectorRef.current = null;
      return;
    }
    const sid = Number(selectedSectorId);
    // Recarga depois de registrar valor/pesos: atualiza no lugar, sem esconder a tela (só a troca de conexão mostra o carregando).
    const silent = loadedSectorRef.current === sid;
    loadedSectorRef.current = sid;
    if (!silent) setLoading(true);
    setError(null);
    (async () => {
      const [columnsRes, cardsRes, countsRes, condRes, radarRes] = await Promise.allSettled([
        getColumns(),
        getCards(sid, CARDS_PER_COLUMN),
        getCardColumnStats(sid),
        listColumnConditions(sid),
        getRevenueRadar(sid),
      ]);
      if (!isCurrent()) return;
      setRadar(radarRes.status === 'fulfilled' ? radarRes.value : null);
      setRadarError(radarRes.status === 'rejected' ? 'Não consegui carregar o Radar de Receita.' : null);
      if (columnsRes.status === 'rejected') {
        setBoard(EMPTY_BOARD);
        setError('Não consegui carregar as colunas do CRM.');
        setLoading(false);
        return;
      }
      const cards = cardsRes.status === 'fulfilled' ? cardsRes.value : [];
      const conditions = new Map<string, CrmColumnCondition>(
        (condRes.status === 'fulfilled' ? condRes.value : []).map((c) => [String(c.columnId), c]),
      );
      const columns = [...columnsRes.value].sort((a, b) => a.position - b.position);
      const counts = countsRes.status === 'fulfilled' ? countsRes.value.counts : {};
      const valueBrl = countsRes.status === 'fulfilled' ? countsRes.value.valueBrl : {};
      setBoard({ columns, cards, counts, valueBrl, conditions, moves: new Map() });
      setLoading(false);

      const contactIds = cards.map((c) => c.contactId).filter((id) => id > 0);
      if (contactIds.length === 0) return;
      try {
        const latest = await listLatestMoves(sid, contactIds);
        if (!isCurrent()) return;
        const moves = new Map<number, CrmMoveLog>();
        latest.forEach((m) => { if (m.contactId) moves.set(m.contactId, m); });
        setBoard((prev) => ({ ...prev, moves }));
      } catch {
        /* evidência é complementar */
      }
    })();
  }, [selectedSectorId, reloadKey]);

  /** Tipo efetivo (mesma regra do briefing): sem tipo, a primeira coluna é entrada. */
  const kindOf = (col: Column) =>
    effectiveColumnKind(board.conditions.get(col.id)?.kind ?? col.kind ?? null, col.name, col.id === board.columns[0]?.id);
  const countOf = (col: Column, loaded: number) => board.counts[col.id] ?? loaded;

  const cardsByColumn = useMemo(() => {
    const map = new Map<string, Card[]>();
    board.cards.forEach((c) => {
      const key = String(c.columnId);
      const list = map.get(key) ?? [];
      list.push(c);
      map.set(key, list);
    });
    map.forEach((list) => list.sort((a, b) => a.position - b.position));
    return map;
  }, [board.cards]);

  const kpis = useMemo(() => {
    let total = 0;
    let inProgress = 0;
    let won = 0;
    board.columns.forEach((col) => {
      const n = board.counts[col.id] ?? (cardsByColumn.get(col.id)?.length ?? 0);
      total += n;
      const kind = kindOf(col);
      if (kind === 'ganho') won += n;
      else if (kind !== 'entrada' && kind !== 'perdido') inProgress += n;
    });
    const now = new Date();
    let aiToday = 0;
    board.moves.forEach((m) => {
      if (m.triggerType === 'on_conversation' && isToday(m.createdAt, now)) aiToday += 1;
    });
    return { total, inProgress, won, aiToday };
  }, [board, cardsByColumn]);

  const renderCard = (card: Card, kind: string | null) => {
    const name = card.contact?.name?.trim() || `Contato #${card.contactId}`;
    const open = kind !== 'entrada' && kind !== 'ganho' && kind !== 'perdido';
    const stalled = open && card.isStalled === true && card.daysStalled != null;
    return (
      <article key={card.id} className="mai-kanban-card">
        <div className="mai-kanban-card-head">
          <span className={`mai-kanban-avatar ${avatarTone(name)}`} aria-hidden>
            {initials(name)}
          </span>
          <div className="mai-kanban-card-titles">
            <strong>{name}</strong>
            {card.title && <span>{card.title}</span>}
          </div>
        </div>
        {(open || card.estimatedValue != null || card.temperature) && (
          <div className="rev-card-facts" data-testid={`card-facts-${card.id}`}>
            {card.estimatedValue != null ? (
              <span className="rev-card-value">{formatMoney(card.estimatedValue, card.currency)}</span>
            ) : open ? (
              <span className="rev-card-value is-empty">Sem valor</span>
            ) : null}
            <TemperatureChip temperature={card.temperature} evidence={card.temperatureEvidence} source={card.temperatureSource} compact />
            {stalled && (
              <span className="rev-card-stalled" title="Sem fala do cliente nem mudança de etapa">
                <FiClock size={11} aria-hidden /> parada há {daysLabel(card.daysStalled!)}
              </span>
            )}
          </div>
        )}
        <CardMoveBadge move={board.moves.get(card.contactId)} tone="light" />
        <div className="mai-kanban-card-foot">
          <div className="mai-kanban-actions">
            <button
              type="button"
              className="mai-btn mai-btn-soft"
              onClick={() => navigate(`/chat?contactId=${card.contactId}`)}
            >
              <FiMessageCircle size={14} aria-hidden /> <span>Conversa</span>
            </button>
            <button type="button" className="mai-btn mai-btn-ghost" onClick={() => navigate(`/crm/oportunidade/${card.id}`)}>
              <FiExternalLink size={14} aria-hidden /> <span>Abrir ficha</span>
            </button>
          </div>
        </div>
      </article>
    );
  };

  const hasColumns = board.columns.length > 0;

  return (
    <div className={embedded ? 'mai-today-embed' : 'mai-page mai-funil-page'}>
      <div className="mai-today-embed-head">
        <div>
          <h2>{embedded ? 'Funil' : 'Funil inteligente'}</h2>
          <p>
            O CRM é único: as etapas são as colunas do CRM e a IA move os cards a partir da conversa, guardando a
            evidência de cada mudança.
          </p>
        </div>
        <div className="mai-btn-row" style={{ marginTop: 0 }}>
          <button type="button" className="mai-btn mai-btn-soft" onClick={() => navigate('/crm')}>
            <FiExternalLink size={14} style={{ marginRight: 6 }} aria-hidden />
            Abrir no CRM
          </button>
        </div>
      </div>

      {!loading && selectedSectorId && hasColumns && (
        <div className="mai-funil-kpis mai-funil-kpis-4" aria-label="Resumo do funil">
          <div className="mai-funil-kpi">
            <strong>{kpis.total}</strong>
            <span>Contatos no funil</span>
          </div>
          <div className="mai-funil-kpi">
            <strong>{kpis.inProgress}</strong>
            <span>Em andamento</span>
          </div>
          <div className="mai-funil-kpi">
            <strong>{kpis.aiToday}</strong>
            <span>Atualizadas pela IA hoje</span>
          </div>
          <div className="mai-funil-kpi">
            <strong>{kpis.won}</strong>
            <span>Ganhos</span>
          </div>
        </div>
      )}

      {!loading && selectedSectorId && hasColumns && (
        <RevenueRadar
          sectorId={Number(selectedSectorId)}
          radar={radar}
          error={radarError}
          initialList={initialList}
          onReload={() => setReloadKey((k) => k + 1)}
          onOpen={(path) => navigate(path)}
        />
      )}

      {!selectedSectorId && (
        <div className="mai-empty-state">
          <p className="mai-empty-state-body">Selecione uma conexão no menu para carregar o funil.</p>
        </div>
      )}
      {loading && selectedSectorId && (
        <div className="mai-empty-state is-loading" aria-busy="true">
          <span className="mai-skeleton-bar" />
          <span className="mai-skeleton-bar is-short" />
        </div>
      )}
      {!loading && error && (
        <p className="mai-toast mai-toast-error" role="alert">{error}</p>
      )}
      {!loading && !error && selectedSectorId && !hasColumns && (
        <div className="mai-empty-state">
          <p className="mai-empty-state-body">
            O CRM ainda não tem colunas. Crie as etapas no CRM (ou use um modelo de funil) para acompanhar aqui.
          </p>
        </div>
      )}

      {!loading && hasColumns && (
        <div className="mai-kanban" role="list">
          {board.columns.map((col) => {
            const cards = cardsByColumn.get(col.id) ?? [];
            const kind = kindOf(col);
            const cond = board.conditions.get(col.id);
            const condText = cond?.enabled && cond.condition?.trim() ? truncateText(cond.condition, 200) : '';
            const total = countOf(col, cards.length);
            return (
              <section key={col.id} className="mai-kanban-col" role="listitem">
                <header className="mai-kanban-col-head" title={condText ? `Condição: ${condText}` : 'Sem condição'}>
                  <span>
                    {col.name}
                    {kind && kind !== 'etapa' && (
                      <span className="mai-kanban-stage-label"> · {columnKindLabel(kind)}</span>
                    )}
                    {(board.valueBrl[col.id] ?? 0) > 0 && (
                      <span className="rev-col-total" data-testid={`column-total-${col.id}`}>{formatBrl(board.valueBrl[col.id])}</span>
                    )}
                  </span>
                  <span className="mai-kanban-count">{total}</span>
                </header>
                <div className="mai-kanban-col-body">
                  {cards.map((c) => renderCard(c, kind))}
                  {cards.length === 0 && <div className="mai-kanban-empty">Vazio</div>}
                  {total > cards.length && (
                    <button type="button" className="mai-linkish" onClick={() => navigate('/crm')}>
                      +{total - cards.length} no CRM →
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OpportunitiesPage;
